import React from 'react';
import { lazily } from 'react-lazily';
import { NumericFormat } from 'react-number-format';
import { CurrencyAmount } from 'src/components/currency-amount';
import { INSTALLMENT_PLANS } from 'src/constants/learn-more';
import { useAppContext } from 'src/context/store/app-provider';
import { useIsRamadanDefaultViewExp } from 'src/hooks/use-is-ramadan-exp';
import { useLocale } from 'src/hooks/use-locale';
import { CurrencyType } from 'src/types/currency';
import { createNormalizeCheckingNumber } from 'src/utils/create-normalize-checking-number';
import { getFormattedAmount } from 'src/utils/get-formatted-amount';
import { getPriceSplitOf } from 'src/utils/get-price-split-of';

const { SnippetDefault } = lazily(
  () => import('src/components/snippet-default')
);

interface Props {
  onClick: () => void;
}

export const DEFAULT_RAMADAN_INSTALLMENTS_COUNT = 6;

export const NonLtfSnippet = ({ onClick }: Props) => {
  const {
    currency,
    price,
    installmentsCount: defaultInstallmentsCount,
    formattedCurrency,
    priceRaw,
    isRtl,
    offers: { installments: offersInstallments = [] },
  } = useAppContext();

  const t = useLocale();

  const isRamadanDefaultViewExp = useIsRamadanDefaultViewExp();
  const backendInstallment =
    offersInstallments.find(
      ({ paymentsCount }) => paymentsCount === INSTALLMENT_PLANS.four
    ) ?? offersInstallments[0];
  const fallbackInstallmentsCount = isRamadanDefaultViewExp
    ? DEFAULT_RAMADAN_INSTALLMENTS_COUNT
    : defaultInstallmentsCount;
  const installmentsCount =
    backendInstallment?.paymentsCount ?? fallbackInstallmentsCount;
  const pricePart = backendInstallment
    ? getFormattedAmount(backendInstallment.amount, currency)
    : getPriceSplitOf(price, currency, installmentsCount);
  const isKSA = formattedCurrency === CurrencyType.SAR;

  const preparedPrice = createNormalizeCheckingNumber(
    'Price argument is not a number',
    0
  )(priceRaw);
  const hasPaymentAmount =
    Boolean(backendInstallment) || Boolean(preparedPrice);

  const getText = (formattedValue: string) => {
    const shariahText = isKSA ? `${t('default.shariahCompliant')}.` : '';
    const amount = () => (
      <CurrencyAmount
        currency={currency}
        currencyPosition={isRtl ? 'end' : 'start'}
      >
        {formattedValue}
      </CurrencyAmount>
    );

    switch (true) {
      case !hasPaymentAmount || !installmentsCount:
        return (
          <>
            {t('default.split_your_purchases_interest_free', {
              installmentsCount,
            })}
            {'. '}
            {shariahText}
          </>
        );
      case isKSA && isRamadanDefaultViewExp:
        return (
          <>
            {t(
              'split_your_purchase_into_x_interest_free_payments',
              { installmentsCount },
              { amount }
            )}{' '}
            {shariahText}
          </>
        );
      case isKSA:
        return (
          <>
            {t(
              'n_interest_free_payments',
              { installmentsCount },
              {
                amount,
              }
            )}{' '}
            {shariahText}
          </>
        );
      default:
        return t(
          'n_interest_free_payments',
          { installmentsCount },
          {
            amount,
          }
        );
    }
  };

  return (
    <SnippetDefault
      message={
        <NumericFormat
          /* Need key to recall renderText with new pricePart */
          thousandSeparator=","
          key={pricePart}
          value={pricePart}
          renderText={getText}
          displayType="text"
          allowNegative={false}
        />
      }
      linkText={t('default.learnMore')}
      onClick={onClick}
    />
  );
};
